// 9fbef606107a605d69c0edbcd8029e5d
import { formValueSelector, getFormSyncErrors } from 'redux-form';
import { createSelector } from 'reselect';
import constants from './CreditCard.constants';
import CARD_RANGES from '../../../../../account/AddEditCreditCard/container/AddEditCreditCard.constants';
import CheckoutSelectors, { getApprovedPLCCCardNumber } from '../../../container/Checkout.selector';
import {
  getCardListState,
  getPLCCPaymentEnabled,
} from '../../../../../account/Payment/container/Payment.selectors';
import { getSelectedCard, isCardNumberExistsInDefinedRange } from '../../../util/utility';
import { getCurrentCountry } from '../../../../../../../reduxStore/selectors/session.selectors';
import SinglePageSelector, {
  getBillingFormFullName,
  getCardTypeRadio,
} from '../../../../SinglePageCheckout/container/SinglePageCheckout.selector';
import { CARD_TYPES } from '../../../../SinglePageCheckout/container/SinglePageCheckout.constants';

const { getBillingValues, getShippingDestinationValues } = CheckoutSelectors;

const getOnFileCardKey = (state) => {
  const formName = getBillingFormFullName(state, constants.FORM_NAME);
  const selector = formValueSelector(formName);
  return selector(state, 'onFileCardKey');
};

const getPaymentMethodId = (state) => {
  const formName = getBillingFormFullName(state, constants.FORM_NAME);
  const selector = formValueSelector(formName);
  return selector(state, 'paymentMethodId');
};

const getCardNumber = (state) => {
  const formName = getBillingFormFullName(state, constants.FORM_NAME);
  const selector = formValueSelector(formName);
  const cardNumber = selector(state, 'cardNumber');
  return cardNumber?.replace(/ /g, ''); // Removing all spaces those were introduced due to card number formatting.
};

// eslint-disable-next-line no-unused-vars
const getEditFormCardNumber = (state) => {
  const selector = formValueSelector(constants.EDIT_FORM_NAME);
  return selector(state, 'cardNumber');
};

const getSyncError = (state) => {
  const formName = getBillingFormFullName(state, constants.FORM_NAME);
  return {
    syncError: getFormSyncErrors(formName)(state),
  };
};

const calcCardType = (
  formCardNumber,
  defaultCardType,
  cardTypeRadio,
  isSPC,
  approvedPLCCCardNumber
) => {
  if ((formCardNumber || '').length === 0) {
    return null;
  }
  const cardNumber = formCardNumber;
  if (cardNumber.startsWith('*')) {
    if (defaultCardType) {
      return defaultCardType;
    }
    if (approvedPLCCCardNumber) {
      return CARD_TYPES.PLCC_CARD;
    }
  }
  // look up based on cardNumber
  const allCardTypes = Object.keys(CARD_RANGES.CREDIT_CARDS_BIN_RANGES);
  const type = allCardTypes.filter((cardType) =>
    isCardNumberExistsInDefinedRange(cardType, cardNumber)
  );

  if (type.length > 0) {
    return CARD_RANGES.ACCEPTED_CREDIT_CARDS[type[0]];
  }

  return null;
};

const getCardType = createSelector(
  [
    getCardNumber,
    (state) => {
      const billingData = getBillingValues(state);
      return billingData && billingData.billing && billingData.billing.cardType;
    },
    getCardTypeRadio,
    SinglePageSelector.checkIsSinglePageCheckout,
    getApprovedPLCCCardNumber,
  ],
  calcCardType
);

const getDefaultCardType = (state, savedCardType) => {
  if (savedCardType) {
    return savedCardType;
  }
  const billingData = getBillingValues(state);
  return billingData && billingData.billing && billingData.billing.cardType;
};

const getFormCardType = (state, savedCardType) => {
  const cardNumber = getCardNumber(state);
  const defaultCardType = getDefaultCardType(state, savedCardType);
  const cardTypeRadio = getCardTypeRadio(state);
  const isSinglePageCheckout = SinglePageSelector.checkIsSinglePageCheckout(state);
  const approvedPLCCCardNumber = getApprovedPLCCCardNumber(state);
  return calcCardType(
    cardNumber,
    defaultCardType,
    cardTypeRadio,
    isSinglePageCheckout,
    approvedPLCCCardNumber
  );
};

const getCardAddress = createSelector(
  [
    getCardNumber,
    (state) => {
      const billingData = getBillingValues(state);
      return billingData && billingData.address && billingData.address.country;
    },
  ],
  () => {
    return null;
  }
);

const getCurrentEditCardType = (state) => {
  const onFileCardKey = getOnFileCardKey(state);
  const cardList = getCardListState(state);
  if (onFileCardKey) {
    const selectedCard = getSelectedCard({ creditCardList: cardList, onFileCardKey });
    return selectedCard && selectedCard.ccType?.toUpperCase();
  }
  return null;
};

const getEditFormCardType = createSelector(
  [
    getCardNumber,
    getCurrentEditCardType,
    getCardTypeRadio,
    SinglePageSelector.checkIsSinglePageCheckout,
    getApprovedPLCCCardNumber,
  ],
  calcCardType
);

const getSameAsShippingValue = (state) => {
  const formName = getBillingFormFullName(state, constants.FORM_NAME);
  const selector = formValueSelector(formName);
  return selector(state, 'sameAsShipping');
};

const getEditFormSameAsShippingValue = (state) => {
  const selector = formValueSelector(constants.EDIT_FORM_NAME);
  return selector(state, 'sameAsShipping');
};

const getSaveToAccountValue = (state) => {
  const formName = getBillingFormFullName(state, constants.FORM_NAME);
  const selector = formValueSelector(formName);
  return selector(state, 'saveToAccount');
};

const getDefaultPaymentValue = (state) => {
  const formName = getBillingFormFullName(state, constants.FORM_NAME);
  const selector = formValueSelector(formName);
  return selector(state, 'defaultPayment');
};

const getSelectedShippingAddressId = createSelector(
  getShippingDestinationValues,
  (shippingData) => {
    return shippingData?.address?.addressId;
  }
);

const getShippingOnFileAddressKey = createSelector(getShippingDestinationValues, (shippingData) => {
  return shippingData?.onFileAddressKey;
});

const getShippingOnFileAddressId = createSelector(getShippingDestinationValues, (shippingData) => {
  return shippingData?.onFileAddressId;
});

const getSelectedOnFileAddressId = (state) => {
  const formName = getBillingFormFullName(state, constants.FORM_NAME);
  const selector = formValueSelector(formName);
  return selector(state, 'onFileAddressId');
};

const getEditFormSelectedOnFileAddressId = (state) => {
  const selector = formValueSelector(constants.FORM_NAME);
  return selector(state, 'onFileAddressId');
};

const getIsPLCCEnabled = (state, isFromBilling = false) => {
  const countryA = getCardAddress(state);
  const formName = getBillingFormFullName(state, constants.FORM_NAME);
  const selector = formValueSelector(formName);
  const country = countryA || selector(state, 'address.country') || getCurrentCountry(state);
  return country === 'US' && isFromBilling ? getPLCCPaymentEnabled(state) : true;
};

const getSelectedCountry = (state) => {
  const formName = getBillingFormFullName(state, constants.FORM_NAME);
  const selector = formValueSelector(formName);
  return selector(state, 'address.country');
};

const getCCAddressFormValue = (state) => {
  const formName = getBillingFormFullName(state, constants.FORM_NAME);
  const selector = formValueSelector(formName);
  return selector(state, 'address');
};

export default {
  getOnFileCardKey,
  getPaymentMethodId,
  getSyncError,
  getCardType,
  getEditFormCardType,
  getSameAsShippingValue,
  getSaveToAccountValue,
  getDefaultPaymentValue,
  getSelectedShippingAddressId,
  getShippingOnFileAddressKey,
  getSelectedOnFileAddressId,
  getShippingOnFileAddressId,
  getEditFormSameAsShippingValue,
  getEditFormSelectedOnFileAddressId,
  getIsPLCCEnabled,
  getSelectedCountry,
  getCurrentEditCardType,
  calcCardType,
  getCCAddressFormValue,
  getFormCardType,
};
